import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  StyleSheet,
  View,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
  NativeSyntheticEvent,
  NativeScrollEvent,
  LayoutChangeEvent,
} from 'react-native';
import { Text, ProgressBar } from 'react-native-paper';
import { useMarkdown, type MarkedStyles } from 'react-native-marked';
import { useTranslation } from 'react-i18next';
import { useAppColors } from '../../hooks/useAppColors';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';

interface LegalDocumentViewerProps {
  url: string;
  onReachedEnd?: () => void;
  onError?: (message: string) => void;
}

const BASE_FONT = 17;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.25;

/**
 * Build the `react-native-marked` style map for the current zoom level.
 *
 * Zoom simply multiplies the font sizes; because the document is rendered with
 * real native text components, enlarging the font reflows the text to the
 * available width — no horizontal scrolling, unlike the old fixed-layout PDF.
 */
function buildMarkedStyles(zoom: number, textColor: string, linkColor: string, borderColor: string): MarkedStyles {
  const fs = (mult: number) => Math.round(BASE_FONT * zoom * mult);
  const body = fs(1);
  return {
    text: { color: textColor, fontSize: body, lineHeight: Math.round(body * 1.6) },
    paragraph: { paddingVertical: 6, marginBottom: 4 },
    li: { color: textColor, fontSize: body, lineHeight: Math.round(body * 1.5) },
    h1: { color: textColor, fontSize: fs(1.55), lineHeight: Math.round(fs(1.55) * 1.3), fontWeight: '700', marginTop: 8, marginBottom: 8 },
    h2: { color: textColor, fontSize: fs(1.25), lineHeight: Math.round(fs(1.25) * 1.35), fontWeight: '700', marginTop: 14, marginBottom: 6 },
    h3: { color: textColor, fontSize: fs(1.08), fontWeight: '700', marginTop: 12, marginBottom: 5 },
    h4: { color: textColor, fontSize: body, fontWeight: '700', marginTop: 10, marginBottom: 4 },
    h5: { color: textColor, fontSize: body, fontWeight: '700', marginTop: 10, marginBottom: 4 },
    h6: { color: textColor, fontSize: body, fontWeight: '700', marginTop: 10, marginBottom: 4 },
    strong: { color: textColor, fontWeight: '700' },
    em: { color: textColor, fontStyle: 'italic' },
    link: { color: linkColor, fontSize: body, textDecorationLine: 'underline' },
    codespan: { fontSize: Math.round(body * 0.9), color: textColor },
    hr: { backgroundColor: borderColor, height: StyleSheet.hairlineWidth, marginVertical: 16 },
    blockquote: { borderLeftWidth: 3, borderLeftColor: borderColor, paddingLeft: 12, marginVertical: 8 },
    tableCell: { padding: 8 },
  };
}

export default function LegalDocumentViewer({ url, onReachedEnd, onError }: LegalDocumentViewerProps) {
  const { t } = useTranslation();
  const colors = useAppColors();
  const [progress, setProgress] = useState(0);
  const [atBottom, setAtBottom] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [markdown, setMarkdown] = useState<string | null>(null);
  const reachedEndRef = useRef(false);
  const viewportHeightRef = useRef(0);
  const contentHeightRef = useRef(0);

  // Fetch the raw Markdown document.
  useEffect(() => {
    reachedEndRef.current = false;
    viewportHeightRef.current = 0;
    contentHeightRef.current = 0;
    setAtBottom(false);
    setProgress(0);
    setErrorMsg(null);
    setZoom(1);
    setMarkdown(null);

    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(url);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const md = await resp.text();
        if (!cancelled) setMarkdown(md);
      } catch (err: any) {
        if (cancelled) return;
        const msg = err?.message ? `${t('legal.loadError')} (${err.message})` : t('legal.loadError');
        setErrorMsg(msg);
        onError?.(msg);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [url, t, onError]);

  // Render the Markdown to native components. The hook must run unconditionally,
  // so we pass an empty string until the document has been fetched.
  const markedStyles = useMemo(
    () => buildMarkedStyles(zoom, colors.text, colors.accent, colors.border),
    [zoom, colors.text, colors.accent, colors.border],
  );
  const elements = useMarkdown(markdown ?? '', {
    styles: markedStyles,
    theme: {
      colors: { text: colors.text, link: colors.accent, code: colors.text, border: colors.border },
    },
  });

  const markReachedEnd = useCallback(() => {
    if (reachedEndRef.current) return;
    reachedEndRef.current = true;
    setAtBottom(true);
    onReachedEnd?.();
  }, [onReachedEnd]);

  // If the rendered document is shorter than the viewport there is nothing to
  // scroll, so the signing gate should open immediately.
  const checkFits = useCallback(() => {
    const vh = viewportHeightRef.current;
    const ch = contentHeightRef.current;
    if (vh > 0 && ch > 0 && ch <= vh + 1) {
      setProgress(1);
      markReachedEnd();
    }
  }, [markReachedEnd]);

  const handleScroll = useCallback((e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
    const scrollable = contentSize.height - layoutMeasurement.height;
    const pct = scrollable > 0 ? Math.min(1, contentOffset.y / scrollable) : 1;
    setProgress(pct);
    if (pct >= 0.99 || scrollable <= 0) markReachedEnd();
  }, [markReachedEnd]);

  const handleLayout = useCallback((e: LayoutChangeEvent) => {
    viewportHeightRef.current = e.nativeEvent.layout.height;
    checkFits();
  }, [checkFits]);

  const handleContentSizeChange = useCallback((_w: number, h: number) => {
    contentHeightRef.current = h;
    checkFits();
  }, [checkFits]);

  const zoomIn = () => setZoom((z) => Math.min(z + ZOOM_STEP, ZOOM_MAX));
  const zoomOut = () => setZoom((z) => Math.max(z - ZOOM_STEP, ZOOM_MIN));

  const renderZoomControls = () => (
    <View style={[styles.zoomControls, { backgroundColor: colors.bgHeader, borderColor: colors.border }]}>
      <TouchableOpacity onPress={zoomOut} style={styles.zoomBtn} hitSlop={8} disabled={zoom <= ZOOM_MIN}>
        <Icon name="minus" size={18} color={zoom <= ZOOM_MIN ? colors.textTertiary : colors.text} />
      </TouchableOpacity>
      <Text style={[styles.zoomLabel, { color: colors.text }]}>{Math.round(zoom * 100)}%</Text>
      <TouchableOpacity onPress={zoomIn} style={styles.zoomBtn} hitSlop={8} disabled={zoom >= ZOOM_MAX}>
        <Icon name="plus" size={18} color={zoom >= ZOOM_MAX ? colors.textTertiary : colors.text} />
      </TouchableOpacity>
    </View>
  );

  return (
    <View style={styles.container}>
      <View style={[styles.progressContainer, { backgroundColor: colors.border }]}>
        <ProgressBar progress={progress} color={colors.accent} style={styles.progressBar} />
      </View>

      {errorMsg ? (
        <View style={[styles.errorOverlay, { backgroundColor: colors.bg }]}>
          <Text style={[styles.errorText, { color: colors.error }]}>{errorMsg}</Text>
        </View>
      ) : markdown === null ? (
        <View style={[styles.loadingOverlay, { backgroundColor: colors.bg }]}>
          <ActivityIndicator color={colors.accent} />
        </View>
      ) : (
        <ScrollView
          style={[styles.scroll, { backgroundColor: colors.bg }]}
          contentContainerStyle={styles.scrollContent}
          onScroll={handleScroll}
          onLayout={handleLayout}
          onContentSizeChange={handleContentSizeChange}
          scrollEventThrottle={16}
        >
          {elements}
        </ScrollView>
      )}

      {markdown !== null && !errorMsg && renderZoomControls()}

      {!atBottom && !errorMsg ? (
        <View style={[styles.hint, { backgroundColor: colors.bgHeader }]}>
          <Text style={[styles.hintText, { color: colors.textSecondary }]}>
            {t('legal.scrollHint')}
          </Text>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  scroll: { flex: 1 },
  scrollContent: { paddingHorizontal: 18, paddingTop: 16, paddingBottom: 420, maxWidth: 760, width: '100%', alignSelf: 'center' },
  progressContainer: { height: 4, width: '100%' },
  progressBar: { height: 4 },
  loadingOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorOverlay: {
    position: 'absolute',
    top: 4,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
    zIndex: 10,
  },
  errorText: { fontSize: 14, textAlign: 'center' },
  hint: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    paddingVertical: 10,
    alignItems: 'center',
  },
  hintText: { fontSize: 13, fontWeight: '500' },
  zoomControls: {
    position: 'absolute',
    top: 12,
    right: 12,
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 20,
    borderWidth: 1,
    paddingHorizontal: 4,
    paddingVertical: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
    zIndex: 20,
  },
  zoomBtn: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  zoomLabel: {
    fontSize: 12,
    fontWeight: '600',
    minWidth: 42,
    textAlign: 'center',
  },
});
